/**
 * Forgot / reset password — design 1B on the real Auth recovery contract
 * (ZOY-284): start by EMAIL (uniform acknowledgement, never reveals whether
 * an account exists) → the code is texted to the account's registered
 * mobile → verify yields a short-lived recovery token → complete sets the
 * password and revokes EVERY session, so the flow ends at Email + Password
 * sign-in. Distinct from first-login completion (the temporary password is
 * known there; here it is not).
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import s from './access.module.css';
import {
  Button,
  Machine,
  Notice,
  OtpInput,
  PasswordField,
  PasswordRulesPanel,
  TextField,
} from '@/ui/kit';
import {
  AuthCode,
  completePasswordRecovery,
  startPasswordRecovery,
  verifyPasswordRecovery,
} from '@/core/auth/api';
import { ApiError, NetworkError } from '@/core/api/errors';
import { passwordRules, rulesSatisfied } from './passwordRules';
import { ruleRows } from './passwordRuleRows';
import { formatWait, otpDeliveryFailedFrom } from './otpThrottle';
import { useServerWait } from './useServerWait';

type Step =
  | { kind: 'email' }
  | { kind: 'code' }
  | { kind: 'password'; recoveryToken: string }
  | { kind: 'done' };

export function ForgotPasswordPage() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState('');
  const [banner, setBanner] = useState<React.ReactNode>(null);
  /** ZOY-768: after a 503 the send / resend control waits out Auth's retryAfterSeconds. */
  const sendWait = useServerWait();

  /** Shared failure mapping: network, throttle (server wait), account state. */
  const handleCommon = (err: unknown): boolean => {
    if (err instanceof NetworkError) {
      setBanner(
        <Notice kind="warn" title={t('signin.netTitle')} role="alert">
          {t('signin.netBody')}
        </Notice>,
      );
      return true;
    }
    if (err instanceof ApiError && err.status === 429) {
      setBanner(
        <Notice kind="warn" title={t('otp.throttledTitle')} role="alert">
          {err.retryAfterSeconds !== undefined
            ? t('otp.throttledBody', { wait: formatWait(intl, err.retryAfterSeconds) })
            : null}
          <div style={{ marginBlockStart: 6, fontSize: 12 }}>{t('otp.serverWaitNote')}</div>
        </Notice>,
      );
      return true;
    }
    if (err instanceof ApiError && err.accountState !== undefined) {
      setBanner(
        <Notice kind="err" title={t('signin.inactiveTitle')} role="alert">
          {t('signin.inactiveBody')}
        </Notice>,
      );
      return true;
    }
    return false;
  };

  const start = async () => {
    setBusy(true);
    setBanner(null);
    try {
      await startPasswordRecovery(email.trim());
      setCode('');
      setCodeError(false);
      setStep({ kind: 'code' });
    } catch (err) {
      // ZOY-768: no code was sent — stay where we are (email step, or the
      // code step on a resend), keep the email, say so, and wait out the
      // server's cooldown. The 503 is account-neutral by design, and so is
      // this message.
      const notSent = otpDeliveryFailedFrom(err);
      if (notSent) {
        sendWait.start(notSent.retryAfterSeconds);
        setBanner(
          <Notice kind="err" title={t('otp.notSentTitle')} role="alert">
            {t('otp.notSentBody')}
            {notSent.retryAfterSeconds !== undefined && notSent.retryAfterSeconds > 0 && (
              <div style={{ marginBlockStart: 6 }}>
                {t('otp.resendWait', { wait: formatWait(intl, notSent.retryAfterSeconds) })}
              </div>
            )}
          </Notice>,
        );
        return;
      }
      if (!handleCommon(err)) {
        setBanner(
          <Notice kind="err" role="alert">
            {t('common.failedKept')}
          </Notice>,
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value: string) => {
    setBusy(true);
    setBanner(null);
    setCodeError(false);
    try {
      const verified = await verifyPasswordRecovery(email.trim(), value);
      setPw('');
      setConfirm('');
      setPwError(null);
      setStep({ kind: 'password', recoveryToken: verified.recoveryToken });
    } catch (err) {
      // Invalid / expired / locked are one uniform 401 by design.
      if (!handleCommon(err)) setCodeError(true);
    } finally {
      setBusy(false);
    }
  };

  const complete = async (recoveryToken: string) => {
    setBusy(true);
    setBanner(null);
    setPwError(null);
    try {
      await completePasswordRecovery(recoveryToken, pw);
      setStep({ kind: 'done' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // RECOVERY_INVALID: the token expired or was used — start over from the email.
        setStep({ kind: 'email' });
        setBanner(
          <Notice kind="warn" title={t('forgot.expiredTitle')} role="alert">
            {t('forgot.expiredBody')}
          </Notice>,
        );
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        // ZOY-632: named in the reader's language; the panel shows which rule fails.
        setPwError(
          err.authCode === AuthCode.PasswordUnchanged
            ? (t('signin.setNewUnchanged') as string)
            : (t('signin.setNewRejected') as string),
        );
        return;
      }
      if (!handleCommon(err)) {
        setBanner(
          <Notice kind="err" role="alert">
            {t('common.failedKept')}
          </Notice>,
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const mismatch = confirm !== '' && confirm !== pw;
  const bothMatch = pw !== '' && confirm === pw;

  return (
    <div className={s.page}>
      <div>
        <div className={s.card}>
          <div className={s.stack}>
            {banner}

            {step.kind === 'email' && (
              <>
                <h1 className={s.title}>{t('forgot.title')}</h1>
                <p className={s.sub}>{t('forgot.sub')}</p>
                <form
                  className={s.stack}
                  onSubmit={(e) => {
                    e.preventDefault();
                    void start();
                  }}
                >
                  <TextField
                    label={t('signin.methodEmail')}
                    value={email}
                    onChange={setEmail}
                    help={t('forgot.emailHelp')}
                    machine
                    type="email"
                    autoComplete="email"
                  />
                  <Button
                    type="submit"
                    variant="brand"
                    disabled={busy || email.trim() === '' || sendWait.left > 0}
                    busy={busy}
                  >
                    {sendWait.left > 0
                      ? t('otp.resendWait', { wait: formatWait(intl, sendWait.left) })
                      : t('forgot.cta')}
                  </Button>
                </form>
              </>
            )}

            {step.kind === 'code' && (
              <>
                <h1 className={s.title}>{t('otp.codeTitle')}</h1>
                <p className={s.sub}>
                  {t('forgot.codeSubA')}
                  <Machine>{email.trim()}</Machine>
                  {t('forgot.codeSubB')}
                </p>
                <OtpInput
                  value={code}
                  onChange={(v) => {
                    setCode(v);
                    setCodeError(false);
                  }}
                  onComplete={(v) => void verify(v)}
                  error={codeError}
                  label={t('otp.codeLabel')}
                />
                <div
                  className={s.ctaNote}
                  role={codeError ? 'alert' : undefined}
                  style={codeError ? { color: 'var(--zc-err-ink)' } : undefined}
                >
                  {codeError ? t('otp.invalid') : t('otp.codeHelp')}
                </div>
                <Button
                  type="button"
                  variant="brand"
                  disabled={busy || code.length < 6}
                  busy={busy}
                  onClick={() => void verify(code)}
                >
                  {t('otp.verify')}
                </Button>
                <div className={s.centerRow}>
                  <Button
                    type="button"
                    variant="link"
                    disabled={busy || sendWait.left > 0}
                    onClick={() => void start()}
                  >
                    {sendWait.left > 0
                      ? t('otp.resendWait', { wait: formatWait(intl, sendWait.left) })
                      : t('otp.resend')}
                  </Button>
                </div>
                <div className={s.ctaNote}>{t('otp.serverWaitNote')}</div>
              </>
            )}

            {step.kind === 'password' && (
              <>
                <h1 className={s.title}>{t('forgot.newPwTitle')}</h1>
                <p className={s.sub}>{t('forgot.newPwSub')}</p>
                <form
                  className={s.stack}
                  onSubmit={(e) => {
                    e.preventDefault();
                    void complete(step.recoveryToken);
                  }}
                >
                  <div>
                    <PasswordField
                      label={t('forgot.newPwLabel')}
                      value={pw}
                      onChange={(v) => {
                        setPw(v);
                        setPwError(null);
                      }}
                      help={t('forgot.newPwHelp')}
                      error={pwError ?? undefined}
                      autoComplete="new-password"
                      showWord="show"
                      hideWord="hide"
                    />
                    <PasswordRulesPanel
                      title={t('forgot.rulesTitle')}
                      rules={ruleRows(intl, passwordRules(pw, null), 'previous')}
                      stateWords={{
                        pending: t('pw.rulePending') as string,
                        met: t('pw.ruleMet') as string,
                        unmet: t('pw.ruleUnmet') as string,
                      }}
                      note={t('forgot.rulesNote')}
                    />
                  </div>
                  <PasswordField
                    label={t('forgot.confirmLabel')}
                    value={confirm}
                    onChange={setConfirm}
                    help={t('forgot.confirmHelp')}
                    error={mismatch ? t('forgot.mismatch') : undefined}
                    ok={bothMatch ? t('forgot.matchOk') : undefined}
                    autoComplete="new-password"
                    showWord="show"
                    hideWord="hide"
                  />
                  <Button
                    type="submit"
                    variant="brand"
                    disabled={
                      busy || pw === '' || !bothMatch || !rulesSatisfied(passwordRules(pw, null))
                    }
                    busy={busy}
                  >
                    {t('forgot.setCta')}
                  </Button>
                </form>
              </>
            )}

            {step.kind === 'done' && (
              <>
                <Notice kind="ok" title={t('forgot.doneTitle')}>
                  {t('forgot.doneBody')}
                </Notice>
                <Button
                  type="button"
                  variant="brand"
                  onClick={() => void navigate({ to: '/signin' })}
                >
                  {t('forgot.doneCta')}
                </Button>
              </>
            )}

            <div className={s.centerRow}>
              <Button type="button" variant="link" onClick={() => void navigate({ to: '/signin' })}>
                {t('signin.backToSignin')}
              </Button>
            </div>
          </div>
        </div>
        {step.kind === 'email' && <div className={s.underCard}>{t('forgot.underForgot')}</div>}
      </div>
    </div>
  );
}
